// Photo Admin API client (write endpoints).
// Read endpoints (GET /event-images, GET /event-images/activities) live in
// web/src/api/eventImages.js — kept separate to keep this admin-only file
// localized to the AdminLogin folder.

import { authFetch } from "../../../api/authFetch";

const BASE_URL =
  process.env.REACT_APP_EVENT_IMAGES_API_URL ||
  "https://7vnhfarqxg.execute-api.us-east-1.amazonaws.com/prod";

// Thumbnail-generation service base. Will be swapped at deploy time once the
// real API is provisioned (mirrors the eventLocations PLACEHOLDER pattern).
const THUMB_BASE_URL =
  process.env.REACT_APP_THUMBNAIL_GEN_API_URL ||
  "https://xmt5tw4g3c.execute-api.us-east-1.amazonaws.com/prod";

const jsonHeaders = () => ({ "Content-Type": "application/json" });

const trimmedBase = () => BASE_URL.replace(/\/$/, "");
const trimmedThumbBase = () => THUMB_BASE_URL.replace(/\/$/, "");

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

/**
 * Request a presigned PUT URL for direct-to-S3 upload.
 * @param {{filename:string, contentType:string, sizeBytes:number, activity:string, displayName:string}} params
 * @returns {Promise<{uploadUrl:string, s3Key:string, expiresInSec:number}>}
 */
export const requestUploadUrl = async ({
  filename,
  contentType,
  sizeBytes,
  activity,
  displayName,
}) => {
  const response = await authFetch(`${trimmedBase()}/event-images/upload-url`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({
      filename,
      contentType,
      sizeBytes,
      activity,
      displayName,
    }),
  });

  if (!response.ok) {
    const data = await safeJson(response);
    const msg =
      (data && data.error) ||
      `Failed to request upload URL (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  return response.json();
};

/**
 * Register an uploaded image (creates the DDB row) after successful PUT to S3.
 * @param {{s3Key:string, displayName:string, activity:string, sizeBytes:number, takenAt?:string}} params
 * @returns {Promise<{item:object}>}
 */
export const createImageRecord = async ({
  s3Key,
  displayName,
  activity,
  sizeBytes,
  takenAt,
}) => {
  const body = { s3Key, displayName, activity, sizeBytes };
  if (takenAt) body.takenAt = takenAt;
  const response = await authFetch(`${trimmedBase()}/event-images`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const data = await safeJson(response);
    const msg =
      (data && data.error) ||
      `Failed to register image (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  return response.json();
};

/**
 * Update an existing photo. Accepts any combination of editable fields;
 * the request body is forwarded as-is so callers control which keys are sent.
 * @param {string} s3Key - raw s3Key (will be URL-encoded internally)
 * @param {{displayName?:string, activity?:string, takenAt?:string}} body
 * @returns {Promise<{item:object}>}
 */
export const updateImageRecord = async (s3Key, body) => {
  const encoded = encodeURIComponent(s3Key);
  const response = await authFetch(`${trimmedBase()}/event-images/${encoded}`, {
    method: "PATCH",
    headers: jsonHeaders(),
    body: JSON.stringify(body || {}),
  });

  if (!response.ok) {
    const data = await safeJson(response);
    const msg =
      (data && data.error) ||
      `Failed to update image (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  return response.json();
};

/**
 * Delete an event image by s3Key.
 * @param {string} s3Key
 * @returns {Promise<void>}
 */
export const deleteImageRecord = async (s3Key) => {
  const encoded = encodeURIComponent(s3Key);
  const response = await authFetch(`${trimmedBase()}/event-images/${encoded}`, {
    method: "DELETE",
    headers: jsonHeaders(),
  });

  if (!response.ok && response.status !== 204) {
    const data = await safeJson(response);
    const msg =
      (data && data.error) ||
      `Failed to delete image (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }
};

/**
 * Trigger a backfill of `_thumb.jpg` thumbnails for any S3 photo missing one.
 * Backend (separate Lambda + API) walks the photos bucket, downsizes any
 * original that doesn't already have a sibling `_thumb.jpg`, and uploads.
 *
 * @returns {Promise<{generated:number, skipped:number, errors?:Array}>}
 */
export const runThumbnailBackfill = async () => {
  if (!THUMB_BASE_URL || THUMB_BASE_URL.includes("PLACEHOLDER")) {
    throw new Error(
      "Thumbnail API not configured (REACT_APP_THUMBNAIL_GEN_API_URL is unset)."
    );
  }
  const response = await authFetch(
    `${trimmedThumbBase()}/thumbnails/backfill`,
    {
      method: "POST",
      headers: jsonHeaders(),
      body: "{}",
    }
  );

  if (!response.ok) {
    const data = await safeJson(response);
    const msg =
      (data && (data.error || data.message)) ||
      `Failed to run thumbnail backfill (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }

  return response.json();
};
